import { twMerge } from 'tailwind-merge';

import { GlassBox } from 'components/glassBox/GlassBox';

import checkIcon from 'assets/icons/daily-rewards/check.svg';
import closeIcon from 'assets/icons/daily-rewards/close.svg';
import fireIcon from 'assets/icons/daily-rewards/fire.svg';

import styles from './CalendarDay.module.scss';

export type CalendarDayVariant = 'upcoming' | 'claimed' | 'missed' | 'current' | 'otherMonth';

type CalendarDayProps = {
  label: string;
  variant: CalendarDayVariant;
  rewardImage?: string;
  rewardLabel?: string;
  vipMultiplier?: string;
  onClick?: () => void;
};

const variantConfig = {
  upcoming: {
    textColor: 'tw-text-white',
    lineColor: 'tw-bg-[#2D5EFF]',
    borderColor: '',
    useGlassBox: true,
    gradient: 'linear-gradient(to bottom right, #2D5EFF 0%, rgba(45, 94, 255, 0.2) 100%)',
    shadowVariant: 'blue' as const,
    icon: null,
    stripeColor: null,
  },
  claimed: {
    textColor: 'tw-text-[#20AA67]',
    lineColor: 'tw-bg-[#20AA67]',
    borderColor: 'tw-border-[#20AA67]',
    useGlassBox: false,
    gradient: '',
    shadowVariant: 'green' as const,
    icon: checkIcon,
    stripeColor: '#20AA67',
  },
  missed: {
    textColor: 'tw-text-[#F83A56]',
    lineColor: 'tw-bg-[#F83A56]',
    borderColor: 'tw-border-[#F83A56]',
    useGlassBox: false,
    gradient: '',
    shadowVariant: 'red' as const,
    icon: closeIcon,
    stripeColor: '#F83A56',
  },
  current: {
    textColor: 'tw-text-white',
    lineColor: 'tw-bg-[#FF8000]',
    borderColor: 'tw-border-[#FF8000]',
    useGlassBox: false,
    gradient: '',
    shadowVariant: 'orange' as const,
    icon: fireIcon,
    stripeColor: null,
  },
  otherMonth: {
    textColor: 'tw-text-[#4E4E4E]',
    lineColor: 'tw-bg-[#4E4E4E]',
    borderColor: 'tw-border-[#4E4E4E]',
    useGlassBox: false,
    gradient: '',
    shadowVariant: 'grey' as const,
    icon: null,
    stripeColor: '#4E4E4E',
  },
};

type VariantConfig = (typeof variantConfig)[CalendarDayVariant];

const getStripeBackground = (color: string) =>
  `repeating-linear-gradient(-45deg, transparent, transparent 4px, ${color}26 5px, ${color}26 6px)`;

type DayContentProps = {
  label: string;
  config: VariantConfig;
  rewardImage?: string;
  rewardLabel?: string;
  vipMultiplier?: string;
  onClick?: () => void;
};

const DayContent = ({ label, config, rewardImage, rewardLabel, vipMultiplier, onClick }: DayContentProps) => (
  <div
    className="tw-relative tw-flex tw-h-full tw-w-full tw-flex-col tw-items-center tw-justify-center tw-gap-[10px] tw-p-[10px]"
    onClick={onClick}
  >
    {config.stripeColor && (
      <div
        className="tw-pointer-events-none tw-absolute tw-inset-0 tw-rounded-[20px]"
        style={{ background: getStripeBackground(config.stripeColor) }}
      />
    )}

    {config.icon && (
      <img src={config.icon} alt="" className="tw-absolute tw-left-[-6px] tw-top-[-6px] tw-h-[24px] tw-w-[24px]" />
    )}

    {vipMultiplier && (
      <span className="tw-absolute tw--right-[5px] tw--top-[5px] tw-flex tw-items-center tw-justify-center tw-rounded-full tw-bg-white tw-px-1 tw-py-0.5 tw-text-[9px] tw-font-bold tw-leading-none tw-text-[#0e2659] lg:tw-text-[10px]">
        {String(Number(vipMultiplier))}x
      </span>
    )}

    <span className={`tw-text-[13px] tw-font-medium lg:tw-text-[16px] ${config.textColor}`}>{label}</span>
    <div className={`tw-h-[1px] tw-w-full tw-shrink-0 ${config.lineColor}`} />

    {rewardImage ? (
      <img src={rewardImage} alt="" className="tw-max-h-[35px] tw-w-full tw-object-contain" />
    ) : rewardLabel ? (
      <span className={`tw-text-[9px] tw-font-medium tw-leading-tight lg:tw-text-[11px] ${config.textColor}`}>
        {rewardLabel}
      </span>
    ) : (
      <div className="tw-relative tw-h-full tw-w-full" />
    )}
  </div>
);

const daySizeClass = 'tw-h-[81.5px] tw-w-[75px] min-[836px]:tw-h-[98px] min-[836px]:tw-w-auto bp-2xl:tw-w-[90px]';

export const CalendarDay = ({ label, variant, rewardImage, rewardLabel, vipMultiplier, onClick }: CalendarDayProps) => {
  const config = variantConfig[variant];

  if (config.useGlassBox) {
    return (
      <GlassBox
        gradient={config.gradient}
        borderRadius="20px"
        borderWidth="1.5px"
        backgroundOpacity={0}
        blurAmount="0px"
        shadowVariant={config.shadowVariant}
        overflowVisible
        className={twMerge(daySizeClass, styles.calendarDay, 'tw-cursor-pointer')}
      >
        <DayContent
          label={label}
          config={config}
          rewardImage={rewardImage}
          rewardLabel={rewardLabel}
          vipMultiplier={vipMultiplier}
          onClick={onClick}
        />
      </GlassBox>
    );
  }

  return (
    <GlassBox
      gradient={config.gradient}
      showBorder={false}
      borderRadius="20px"
      backgroundOpacity={0}
      blurAmount="0px"
      shadowVariant={config.shadowVariant}
      overflowVisible
      className={twMerge(
        daySizeClass,
        styles.calendarDay,
        'tw-cursor-pointer tw-border-[1.5px] tw-border-solid',
        config.borderColor
      )}
    >
      <DayContent
        label={label}
        config={config}
        rewardImage={rewardImage}
        rewardLabel={rewardLabel}
        vipMultiplier={vipMultiplier}
        onClick={onClick}
      />
    </GlassBox>
  );
};
